import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ModalMultiUse } from '@sentara-health/optimahealth_web';
import {
  useIdleSurveyModal,
  IDLE_SURVEY_ORIGIN,
  domainForSegment,
} from 'common';
import { useAdobeAnalytics } from '@sentara-health/platform-analytics';

const IdleSurveyModal: React.FC = () => {
  const { isOpen, surveyUrl, closeModal } = useIdleSurveyModal();
  const { t } = useTranslation();
  const { contentListingItemClicked } = useAdobeAnalytics();
  const hasTrackedOpen = useRef(false);
  const [iframeLoaded, setIframeLoaded] = useState(false);

  const buildContentListing = (contentId: string, contentTitle: string) => ({
    segment: `${domainForSegment}${globalThis.location.pathname}`,
    contentListing: {
      eventType: t('idleSurveyModal.contentListingEventType'),
      listing: {
        content: [{ contentId, contentTitle, itemPosition: 1 }],
      },
    },
  });

  // Track modal open
  useEffect(() => {
    if (isOpen && !hasTrackedOpen.current) {
      hasTrackedOpen.current = true;
      contentListingItemClicked(
        buildContentListing(
          t('idleSurveyModal.surveyModalOpenedId'),
          t('idleSurveyModal.surveyModalOpenedTitle')
        )
      );
    }
    if (!isOpen) {
      hasTrackedOpen.current = false;
      setIframeLoaded(false);
    }
  }, [isOpen, contentListingItemClicked]);

  const handleClose = () => {
    contentListingItemClicked(
      buildContentListing(
        t('idleSurveyModal.surveyModalClosedId'),
        t('idleSurveyModal.surveyModalClosedTitle')
      )
    );
    closeModal();
  };

  useEffect(() => {
    if (!isOpen) return;

    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== IDLE_SURVEY_ORIGIN) return;
      if (event.data.Key === 'close') {
        handleClose();
      }
      if (event.data.Key === 'skip') {
        contentListingItemClicked(
          buildContentListing(
            t('idleSurveyModal.surveyFeedbackSkippedId'),
            t('idleSurveyModal.surveyFeedbackSkippedTitle')
          )
        );
      }
      if (event.data.Status === 'Completed') {
        contentListingItemClicked(
          buildContentListing(
            t('idleSurveyModal.surveyFeedbackCompletedId'),
            t('idleSurveyModal.surveyFeedbackCompletedTitle')
          )
        );
      }
      if (event.data.type === 'cf-height-change') {
        const newHeight = event.data.value;
        const iframe = document.querySelector(
          'iframe[data-testid="idleSurveyIframe"]'
        ) as HTMLIFrameElement | null;
        if (iframe) {
          iframe.style.height = `${newHeight + 1}px`;
        }
        setIframeLoaded(true)
      }
    };

    window.addEventListener('message', handleMessage);

    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [isOpen, handleClose]);

  return isOpen ? ( //Modal mounts when isOpen is true 
    // we keep the modal hidden until the iframe has loaded and the height is set
    <div style={{ visibility: iframeLoaded ? 'visible' : 'hidden' }}> 
        <ModalMultiUse
          isOpen
          onClose={handleClose}
          className="modal-lg"
          body={
            surveyUrl ? (
              <iframe
                src={surveyUrl}
                title={t('idleSurveyModal.idleSurveyTitle')}
                data-testid="idleSurveyIframe"                style={{
                  width: '100%',
                  height: '450px',
                  transition: 'height 0.3s ease',
                  overflow: 'hidden',
                }}
              />
            ) : null
          }
          dataTestId="idleSurveyModal"
          variant="largeNoPadBody"
        />
    </div>
  ) : null;
};

export default IdleSurveyModal;
